import { observable } from "mobx"

import {
  type PromptInputModel,
  PromptInputModelImpl,
  type PromptInputSubmitArgs
} from "@/components/PromptInput/PromptInputModel.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { projectsV2QueryKey } from "@/lib/queries.ts"
import { TaskSettingsModel } from "@/routes/tasks/TaskNewScene/TaskSettingsModel.ts"

import type { RouterType } from "@/context.ts"
import type { useInvalidateQueries } from "@/hooks/useRootContext.ts"
import type { AiAgentTask } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"


export class NewProjectFormModel extends Model {
  readonly inputModel: PromptInputModel
  readonly taskSettings = new TaskSettingsModel(true)
  readonly canCreateSampleProject = observable.box<boolean>(false)

  constructor(private readonly context: OrgContext, private readonly invalidateQueries: ReturnType<typeof useInvalidateQueries>, router: RouterType, private readonly onComplete: (projectKey: AiAgentTask) => void) {
    super("NewProjectFormModel")

    void this.loadCanCreateSampleProject()

    this.inputModel = new PromptInputModelImpl({
      name: "CreateProjectInput",
      submit: this.createProject,
      validate: this.validate,
      autoFocus: observable.box(false),
      initProjectStep: observable.box("EnterUrl"),
      completionProvider: async () => [],
      orgContext: context,
      router: router
    })
  }

  readonly validate = async (args: PromptInputSubmitArgs) => {
    const validationResult = await this.context.queries.validateRemoteUrl(args.firstInputValue)
    if (validationResult.valid) {
      return null
    } else {
      return validationResult.error ?? "Invalid URL"
    }
  }

  private readonly loadCanCreateSampleProject = async () => {
    try {
      const canCreate = await this.context.queries.apiClient.canCreateSampleProject()
      this.canCreateSampleProject.set(canCreate)
    } catch (error) {
      log.error("Failed to check whether sample project can be created:", error)
      this.canCreateSampleProject.set(false)
    }
  }

  readonly createProject = async (args: PromptInputSubmitArgs) => {
    const settings = this.taskSettings.getSettings()
    const result = await this.context.queries.apiClient.startProjectCreation(args.firstInputValue, settings)
    void this.invalidateQueries({ queryKey: projectsV2QueryKey })
    this.onComplete(result)
  }
}

const log = getLogger("NewProjectFormModel")
